// src/pages/Dispatch.tsx — every booked job and who's doing it (OPS-FINAL D3).
// Actions go through the dispatch edge functions, which check the operator role and write the log.
// Each action previews what will happen and quotes every text message that will be sent.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  canApply,
  ops,
  STATUS_LABEL,
  type Candidate,
  type JobDetail,
  type JobList,
  type RestartPlan,
} from "../lib/ops";
import { dateTime, money, shortDate, todayPacific, waited } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";
import { Panel } from "../components/Panel";
import { CounterOffers, PayoutSection } from "../components/PayControls";

type View = "open" | "completed";
type Action = "assign" | "cancel" | "reschedule" | "restart";

const CLOSED = ["completed", "cancelled"];
export const canAssign = (d: JobDetail) =>
  !d.maestro && !["completed", "cancelled", "draft", "in_progress"].includes(d.job.status);
export const canCancel = (d: JobDetail) => !CLOSED.includes(d.job.status);
export const canReschedule = (d: JobDetail) => !["completed", "cancelled", "in_progress", "draft"].includes(d.job.status);
export const canRestart = (d: JobDetail) => !d.maestro && ["needs_ops", "cascade_active", "preference_pending"].includes(d.job.status);

// The exact texts the edge functions send (obra-accept-job v12, obra-reschedule-job v7b, obra-start-cascade v8).
export const TEXT_TAKEN_ES =
  "El trabajo que viste ya fue tomado por otro Maestro. Mantente atento — más trabajos llegan pronto. greenmaestro.co/obra";
/** Same formatter as obra-reschedule-job v7b: "Tuesday, Oct 20" / "martes, 20 de oct". */
export function friendlyDate(ymd: string, lang: "en" | "es"): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Intl.DateTimeFormat(lang === "es" ? "es-US" : "en-US", {
    weekday: "long", month: "short", day: "numeric", timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}
export const textRescheduleMaestro = (date: string, lang: string) =>
  lang.startsWith("en")
    ? `📅 A job was rescheduled to ${friendlyDate(date, "en")}. You have 4h to confirm. → gm.co/obra`
    : `📅 Un trabajo fue reprogramado para el ${friendlyDate(date, "es")}. Tienes 4h para confirmar. → gm.co/obra`;
export const textRescheduleHomeowner = (date: string) =>
  `Green Maestro: your visit is now scheduled for ${friendlyDate(date, "en")}. / Su visita ahora está programada para el ${friendlyDate(date, "es")}.`;
export const textBroadcast = (zip: string, rateCents: number, lang: string) => {
  const rate = `$${(rateCents / 100).toFixed(0)}`;
  return lang.startsWith("en")
    ? `🌿 New job available in your area (${zip}) — ${rate}. Accept, counter, or pass. See: gm.co/obra`
    : `🌿 Nuevo trabajo disponible en tu zona (${zip}) — ${rate}. Acepta, oferta tu precio, o pasa. Ver: gm.co/obra`;
};

export default function Dispatch() {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<View>("open");
  const [list, setList] = useState<JobList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const jobId = params.get("job");
  const action = params.get("action") as Action | null;

  const load = useCallback(async (v: View) => {
    setList(null);
    try {
      setList(await ops.listJobs(v));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load(view);
  }, [load, view]);

  const open = (id: string | null, act?: Action | null) => {
    const next = new URLSearchParams();
    if (id) next.set("job", id);
    if (id && act) next.set("action", act);
    setParams(next, { replace: !id });
  };

  const panel = jobId ? (
    <JobPanel
      key={jobId}
      jobId={jobId}
      action={action}
      onAction={(a) => open(jobId, a)}
      onClose={() => open(null)}
      onChanged={() => load(view)}
    />
  ) : undefined;

  const pow = list?.proof_of_work;
  return (
    <PageFrame title="Dispatch" lede="Every booked job and who's doing it." panel={panel}>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Which jobs">
          {(["open", "completed"] as View[]).map((v) => (
            <button key={v} type="button" className={view === v ? "seg on" : "seg"} aria-pressed={view === v} onClick={() => setView(v)}>
              {v === "open" ? "Open" : "Completed"}
            </button>
          ))}
        </div>
        {view === "completed" && pow && (
          <p className="toolbar-note">
            Proof of work: {pow.with_photos} of {pow.completed} completed {pow.completed === 1 ? "job has" : "jobs have"} photos.
          </p>
        )}
      </div>

      {error && <Notice kind="error">{error}</Notice>}
      {!list && !error && <Loading what="jobs" />}
      {list && list.rows.length === 0 && (
        <p className="empty-state">{view === "open" ? "No open jobs." : "No completed jobs yet."}</p>
      )}
      {list && list.rows.length > 0 && (
        <div className="table-scroll">
          <table className="data-table jobs-table">
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col">ZIP</th>
                <th scope="col">Date</th>
                <th scope="col">Status</th>
                <th scope="col">Maestro</th>
                <th scope="col" className="num">Price</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((r) => (
                <tr
                  key={r.id}
                  className={jobId === r.id ? "row-link selected" : "row-link"}
                  tabIndex={0}
                  onClick={() => open(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      open(r.id);
                    }
                  }}
                  aria-label={`Open ${r.service_name ?? "job"} ${r.short_ref}`}
                >
                  <td data-label="Job">
                    <span className="cell-main">{r.service_name ?? r.service_id ?? "Job"}</span>
                    <span className="cell-sub mono">{r.short_ref}</span>
                  </td>
                  <td data-label="ZIP" className="mono">{r.zip ?? "—"}</td>
                  <td data-label="Date">{shortDate(r.scheduled_date)}</td>
                  <td data-label="Status">
                    <StatusChip status={r.status} paid={r.payment_status === "paid"} unassigned={!r.maestro_id} />
                  </td>
                  <td data-label="Maestro">{r.maestro_name ?? <span className="muted">None yet</span>}</td>
                  <td data-label="Price" className="num money">{r.price_cents === null ? <span className="muted not-recorded">Not recorded</span> : money(r.price_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageFrame>
  );
}

function StatusChip({ status, paid, unassigned }: { status: string; paid: boolean; unassigned: boolean }) {
  const label = STATUS_LABEL[status] ?? status.replace(/_/g, " ");
  const urgent = paid && unassigned && !CLOSED.includes(status);
  return <Chip tone={urgent ? "warn" : status === "completed" ? "info" : undefined}>{urgent ? `${label} · paid` : label}</Chip>;
}

// ── Job panel ──────────────────────────────────────────────

function JobPanel(props: {
  jobId: string;
  action: Action | null;
  onAction: (a: Action | null) => void;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await ops.jobDetail(props.jobId));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [props.jobId]);
  useEffect(() => {
    void load();
  }, [load]);

  const done = async () => {
    props.onAction(null);
    await Promise.all([load(), props.onChanged()]);
  };

  const j = detail?.job;
  const title = j ? `${j.service_name ?? "Job"} · ${j.short_ref}` : "Job";
  const act = detail && props.action && isAllowed(detail, props.action) ? props.action : null;

  return (
    <Panel kicker="Job" title={title} onClose={props.onClose}>
      {!detail && !error && <Loading what="the job" />}
      {error && <Notice kind="error">{error}</Notice>}
      {detail && act && (
        <ActionView detail={detail} action={act} onBack={() => props.onAction(null)} onDone={done} />
      )}
      {detail && !act && <JobSummary detail={detail} onAction={props.onAction} onChanged={done} />}
    </Panel>
  );
}

function isAllowed(d: JobDetail, a: Action): boolean {
  if (a === "assign") return canAssign(d);
  if (a === "cancel") return canCancel(d);
  if (a === "reschedule") return canReschedule(d);
  return canRestart(d);
}

function JobSummary({ detail, onAction, onChanged }: { detail: JobDetail; onAction: (a: Action) => void; onChanged: () => void | Promise<void> }) {
  const j = detail.job;
  const [urls, setUrls] = useState<(string | null)[]>([]);
  useEffect(() => {
    let live = true;
    Promise.all(detail.photos.map((p) => ops.photoUrl(p.path))).then((u) => live && setUrls(u));
    return () => {
      live = false;
    };
  }, [detail.photos]);

  const actions: { id: Action; label: string; show: boolean; primary?: boolean }[] = [
    { id: "assign", label: "Assign a Maestro →", show: canAssign(detail), primary: true },
    { id: "restart", label: "Restart the cascade →", show: canRestart(detail) },
    { id: "reschedule", label: "Reschedule →", show: canReschedule(detail) },
    { id: "cancel", label: "Cancel the job →", show: canCancel(detail) },
  ];
  const visible = actions.filter((a) => a.show);

  return (
    <>
      <p className="summary-line">
        <StatusChip status={j.status} paid={false} unassigned={!detail.maestro} /> {detail.paid ? "Paid" : "Not paid"}
      </p>
      <dl className="facts">
        <dt>Date</dt>
        <dd>
          {shortDate(j.scheduled_date)}
          {j.scheduled_time_start ? `, ${j.scheduled_time_start.slice(0, 5)}–${(j.scheduled_time_end ?? "").slice(0, 5)}` : ""}
        </dd>
        <dt>Price charged</dt>
        <dd className="money">{j.price_cents === null ? <span className="muted not-recorded">Not recorded</span> : money(j.price_cents)}</dd>
        <dt>Maestro</dt>
        <dd>
          {detail.maestro ? `${detail.maestro.name ?? "Unnamed"}${detail.maestro.has_phone ? "" : " (no phone on file)"}` : "None yet"}
        </dd>
        <dt>Maestro rate</dt>
        <dd className="money">{money(j.maestro_rate_cents)}</dd>
        <dt>Homeowner</dt>
        <dd>
          {detail.homeowner?.name ?? "—"}
          {detail.homeowner?.phone && (
            <>
              {" · "}
              <a href={`tel:${detail.homeowner.phone.replace(/[^\d+]/g, "")}`}>{detail.homeowner.phone}</a>
            </>
          )}
          {detail.homeowner?.email && (
            <>
              {" · "}
              <a href={`mailto:${detail.homeowner.email}`}>{detail.homeowner.email}</a>
            </>
          )}
        </dd>
        <dt>Property</dt>
        <dd>{detail.property ? [detail.property.address, detail.property.city, detail.property.zip].filter(Boolean).join(", ") : j.zip ?? "—"}</dd>
        <dt>Cascade</dt>
        <dd>
          {j.cascade_step ? `Step ${j.cascade_step}` : "—"}
          {j.step_started_at ? `, started ${waited(j.step_started_at)} ago` : j.cascade_step ? ", no start time recorded" : ""}
        </dd>
      </dl>

      {visible.length > 0 && (
        <div className="action-list">
          {visible.map((a) => (
            <button key={a.id} type="button" className={a.primary ? "btn primary" : "btn"} onClick={() => onAction(a.id)}>
              {a.label}
            </button>
          ))}
        </div>
      )}

      <CounterOffers detail={detail} takenText={TEXT_TAKEN_ES} onChanged={onChanged} />
      <PayoutSection detail={detail} onChanged={onChanged} />

      {detail.open_offers.length > 0 && (
        <>
          <SectionLabel>Open offers</SectionLabel>
          <ul className="plain-list">
            {detail.open_offers.map((o, i) => (
              <li key={i}>
                {o.maestro ?? "A Maestro"} ({o.level ?? "offer"}), expires {o.expires_at ? dateTime(o.expires_at) : "—"}
              </li>
            ))}
          </ul>
        </>
      )}

      <SectionLabel>Timeline</SectionLabel>
      <ol className="timeline">
        {detail.timeline.map((t, i) => (
          <li key={i}>
            <span className="tl-when">{t.at ? dateTime(t.at) : "—"}</span>
            <span className="tl-what">
              {eventLabel(t.event)}
              {t.maestro ? `: ${t.maestro}` : ""}
              {t.rate_cents ? ` · ${money(t.rate_cents)}` : ""}
            </span>
            {t.notes && <span className="tl-notes">{t.notes}</span>}
          </li>
        ))}
      </ol>

      <SectionLabel>Photos</SectionLabel>
      {detail.photos.length === 0 ? (
        <p className="muted">No photos yet.</p>
      ) : (
        <div className="photo-grid">
          {detail.photos.map((p, i) =>
            urls[i] ? (
              <a key={p.path} href={urls[i] as string} target="_blank" rel="noreferrer">
                <img src={urls[i] as string} alt={`Job photo ${i + 1}`} />
              </a>
            ) : (
              <span key={p.path} className="photo-missing">Photo {i + 1}</span>
            ),
          )}
        </div>
      )}
    </>
  );
}

const EVENT_LABEL: Record<string, string> = {
  booked: "Booked",
  assigned: "Maestro confirmed",
  completed: "Completed",
  accepted: "Accepted",
  ops_assigned: "Assigned by an operator",
  ops_restart: "Cascade restarted by an operator",
  ops_cancel: "Cancelled by an operator",
  auto_draw: "Automatic draw",
  auto_assigned: "Auto-assigned",
  auto_confirmed: "Confirmed automatically",
  escalation_round: "Rate raised",
  broadcast_sms_sent: "Broadcast to the ZIP's Maestros",
  preference_offered: "Offered to a preferred Maestro",
  preference_expired: "Offer expired",
  needs_ops: "Sent to operators",
};
const eventLabel = (e: string) => EVENT_LABEL[e] ?? e.replace(/_/g, " ");

// ── Actions ────────────────────────────────────────────────

const ACTION_TITLE: Record<Action, string> = {
  assign: "Assign a Maestro",
  cancel: "Cancel the job",
  reschedule: "Reschedule",
  restart: "Restart the cascade",
};

function ActionView(props: { detail: JobDetail; action: Action; onBack: () => void; onDone: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const allowed = canApply(role);
  const d = props.detail;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // action-specific state
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [date, setDate] = useState(d.job.scheduled_date && d.job.scheduled_date >= todayPacific() ? d.job.scheduled_date : todayPacific());
  const [plan, setPlan] = useState<RestartPlan | null>(null);

  useEffect(() => {
    let live = true;
    if (props.action === "assign") {
      ops.jobCandidates(d.job.id).then((c) => live && setCandidates(c)).catch((e: Error) => live && setError(e.message));
    }
    if (props.action === "restart" && allowed) {
      ops.restartPreview(d.job.id).then((p) => live && setPlan(p)).catch((e: Error) => live && setError(e.message));
    }
    return () => {
      live = false;
    };
  }, [props.action, d.job.id, allowed]);

  const reasonOk = reason.trim().length >= 5;
  const chosen = candidates?.find((c) => c.id === pick) ?? null;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      if (props.action === "assign" && pick) {
        await ops.assign(d.job.id, pick, reason.trim());
        toast("✓ Maestro assigned");
      } else if (props.action === "cancel") {
        await ops.cancel(d.job.id, reason.trim());
        toast("✓ Job cancelled");
      } else if (props.action === "reschedule") {
        await ops.reschedule(d.job.id, date, reason.trim());
        toast("✓ Job rescheduled");
      } else if (props.action === "restart") {
        await ops.restart(d.job.id, reason.trim());
        toast("✓ Cascade restarted");
      }
      await props.onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const texts: { to: string; body: string }[] = [];
  let what = "";
  let confirmLabel = "";
  let ready = reasonOk;

  if (props.action === "assign") {
    confirmLabel = chosen ? `Assign ${chosen.name ?? "this Maestro"}` : "Assign Maestro";
    what = chosen
      ? `Assigns ${chosen.name ?? "this Maestro"} to this job at ${money(d.job.maestro_rate_cents)} and closes every other open offer.`
      : "Pick a Maestro first.";
    if (d.open_offers.length > 0) {
      texts.push({ to: `${d.open_offers.length} Maestro${d.open_offers.length === 1 ? "" : "s"} holding an open offer (Spanish)`, body: TEXT_TAKEN_ES });
    }
    ready = ready && !!chosen;
  } else if (props.action === "cancel") {
    confirmLabel = "Cancel job";
    what = `Marks this job cancelled and closes its open offers.${d.paid ? " It was paid: no refund is issued automatically, so refund it in Stripe if one is owed." : ""}`;
  } else if (props.action === "reschedule") {
    confirmLabel = "Reschedule job";
    const niceDate = date;
    what = d.maestro
      ? `Moves the job to ${shortDate(date)}. ${d.maestro.name ?? "The Maestro"} is taken off it and gets the first right to the new date for 4 hours.`
      : `Moves the job to ${shortDate(date)} and sends it back into the cascade.`;
    if (d.maestro?.has_phone) texts.push({ to: `${d.maestro.name ?? "The Maestro"} (${d.maestro.language.startsWith("en") ? "English" : "Spanish"})`, body: textRescheduleMaestro(niceDate, d.maestro.language) });
    if (d.homeowner?.phone) texts.push({ to: `${d.homeowner.name ?? "The homeowner"} (English + Spanish)`, body: textRescheduleHomeowner(niceDate) });
    ready = ready && date >= todayPacific();
  } else if (props.action === "restart") {
    confirmLabel = "Restart cascade";
    if (plan) {
      what = plan.texts;
      if (plan.restart_at_step === 3) {
        for (const m of plan.maestros) {
          if (m.has_phone) texts.push({ to: `${m.name ?? "A Maestro"} (${m.language === "en" ? "English" : "Spanish"})`, body: textBroadcast(d.job.zip ?? "", plan.rate_cents, m.language) });
        }
      }
    } else {
      what = allowed ? "Checking who would be offered this job…" : "Only pricing admins and the owner can preview a restart.";
    }
    ready = ready && !!plan;
  }

  const noPhones =
    (props.action === "restart" && plan && plan.maestros.length > 0 && plan.maestros.every((m) => !m.has_phone)) ||
    (props.action === "reschedule" && d.maestro && !d.maestro.has_phone);

  return (
    <>
      <button type="button" className="btn quiet back" onClick={props.onBack}>
        Back to the job
      </button>
      <h3 className="action-title">{ACTION_TITLE[props.action]}</h3>

      {props.action === "assign" && (
        <>
          <SectionLabel>Maestros serving {d.job.zip}</SectionLabel>
          {!candidates && !error && <Loading what="Maestros" />}
          {candidates && candidates.length === 0 && <Notice kind="info">No Maestro has this ZIP active.</Notice>}
          {candidates && candidates.length > 0 && (
            <fieldset className="choice">
              <legend className="sr-only">Maestro</legend>
              {candidates.map((c) => (
                <label key={c.id} className={c.blackout ? "is-blocked" : undefined}>
                  <input type="radio" name="maestro" checked={pick === c.id} disabled={c.blackout} onChange={() => setPick(c.id)} />
                  <span>
                    {c.name ?? "Unnamed"} <span className="muted">· {c.titulo ?? "Sin título"} · {c.jobs_completed} jobs{c.has_phone ? "" : " · no phone"}{c.blackout ? " · blacked out that day" : ""}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </>
      )}

      {props.action === "reschedule" && (
        <div className="field">
          <label htmlFor="new-date">New date</label>
          <input id="new-date" type="date" min={todayPacific()} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      )}

      <div className="what-happens">
        <SectionLabel>What will happen</SectionLabel>
        <p>{what}</p>
        {props.action !== "restart" || plan ? (
          texts.length > 0 ? (
            <>
              <p className="texts-head">These texts are sent:</p>
              <ul className="texts">
                {texts.map((t, i) => (
                  <li key={i}>
                    <span className="text-to">To {t.to}</span>
                    <q className="text-body">{t.body}</q>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="texts-head">Nobody is texted.</p>
          )
        ) : null}
        {props.action === "assign" && <p className="hint">The assigned Maestro isn't texted by the system. Call them to confirm.</p>}
        {props.action === "restart" && plan && plan.restart_at_step === 2 && plan.maestros.length > 0 && (
          <ul className="plain-list">
            {plan.maestros.map((m, i) => (
              <li key={i}>
                {m.name ?? "A Maestro"} ({m.language === "en" ? "English" : "Spanish"}){m.has_phone ? "" : ", no phone on file"}
              </li>
            ))}
          </ul>
        )}
        {noPhones && <Notice kind="info">No phone number is on file, so nobody will actually receive a text.</Notice>}
      </div>

      <SectionLabel>Reason</SectionLabel>
      <textarea
        className="reason"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why? This goes in the change log."
        aria-label="Reason"
      />
      {!reasonOk && reason.length > 0 && <p className="hint">At least 5 characters.</p>}
      {error && <Notice kind="error">{error}</Notice>}
      {allowed ? (
        <button
          type="button"
          className={props.action === "cancel" ? "btn danger wide" : "btn primary wide"}
          disabled={!ready || busy}
          onClick={confirm}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      ) : (
        <Notice kind="info">Viewers can see this preview but not act on it.</Notice>
      )}
    </>
  );
}
